import type { BattleEvent, BattleUnit, Side, Winner } from "../../../benny/src/game/model/battle";
import type { Element, GridPos } from "../../../benny/src/game/model/board";
import type { Reward, Run } from "../../../benny/src/game/model/run";

export const PROTOCOL_VERSION = 3;
export type LobbyPlayer = { id: string; name: string };
export type Member = LobbyPlayer & { run: Run | null; rewards: Reward[]; ready: boolean; score: number; connected: boolean };
export type BattleFrame = {
  matchId: string; round: number; sequence: number; elapsed: number;
  units: BattleUnit[]; events: BattleEvent[]; winner: Winner | null;
};
export type MatchSnapshot = {
  id: string; round: number; revision: number; own: Member; other: Member;
  ownSide: Side;
  battle: BattleFrame | null;
  battleStartsAt?: number;
  serverTime?: number;
};
type MatchCommand = { matchId: string; round: number; requestId?: string };
export type ClientMessage =
  | { type: "join"; playerId: string; name: string }
  | { type: "challenge"; targetId: string; requestId?: string }
  | ({ type: "choose"; element: Element } & MatchCommand)
  | ({ type: "arrange"; from: GridPos; to: GridPos } & MatchCommand)
  | ({ type: "reward"; reward: Reward } & MatchCommand)
  | ({ type: "ready" | "attack" } & MatchCommand)
  | ({ type: "chat"; text: string } & MatchCommand)
  | { type: "leave"; requestId?: string }
  | { type: "sync"; requestId?: string }
  | { type: "ping"; requestId?: string };
export type ServerMessage =
  | { type: "welcome"; protocol: number }
  | { type: "lobby"; players: LobbyPlayer[] }
  | { type: "state"; match: MatchSnapshot; requestId?: string }
  | { type: "frame"; frame: BattleFrame }
  | { type: "chat"; matchId: string; id: string; name: string; text: string }
  | { type: "left"; message: string }
  | { type: "error"; message: string; requestId?: string }
  | { type: "pong" };

export function record(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
const pos = (value: unknown): value is GridPos => record(value) && Number.isInteger(value.x) && Number.isInteger(value.y) &&
  Number(value.x) >= 0 && Number(value.x) < 12 && Number(value.y) >= 0 && Number(value.y) < 12;
const element = (value: unknown): value is Element => value === "fire" || value === "ice" || value === "lightning";
export function parseClientMessage(raw: string): ClientMessage {
  const message: unknown = JSON.parse(raw);
  if (!record(message) || typeof message.type !== "string") throw new Error("Invalid multiplayer message.");
  if (message.requestId !== undefined && (typeof message.requestId !== "string" || message.requestId.length > 80)) throw new Error("Invalid request ID.");
  switch (message.type) {
    case "join":
      if (typeof message.playerId !== "string" || !/^[\w-]{1,80}$/.test(message.playerId) || typeof message.name !== "string" || !message.name.trim()) throw new Error("Enter your name to join.");
      break;
    case "challenge":
      if (typeof message.targetId !== "string") throw new Error("Choose an available player.");
      break;
    case "choose": if (!element(message.element)) throw new Error("Choose Fire, Ice, or Lightning."); break;
    case "arrange": if (!pos(message.from) || !pos(message.to)) throw new Error("Choose valid board cells."); break;
    case "reward":
      if (!record(message.reward) || !element(message.reward.element) || !Number.isSafeInteger(message.reward.tier) || Number(message.reward.tier) < 1) throw new Error("Choose an offered elemental.");
      break;
    case "chat": if (typeof message.text !== "string" || message.text.length > 2000) throw new Error("Chat messages can contain up to 160 characters."); break;
    case "ready": case "attack": case "leave": case "sync": case "ping": break;
    default: throw new Error(`Unknown multiplayer command: ${message.type}`);
  }
  if (["choose", "arrange", "reward", "ready", "attack", "chat"].includes(message.type) &&
      (typeof message.matchId !== "string" || !Number.isSafeInteger(message.round))) throw new Error("The match has changed. Reconnect to refresh it.");
  return message as ClientMessage;
}

export function parseServerMessage(raw: string): ServerMessage {
  const message: unknown = JSON.parse(raw);
  if (!record(message)) throw new Error("Invalid multiplayer response.");
  switch (message.type) {
    case "welcome": if (message.protocol !== PROTOCOL_VERSION) throw new Error(`Multiplayer version mismatch: client protocol ${PROTOCOL_VERSION}, server protocol ${String(message.protocol)}. Deploy matching client and worker versions, then reload.`); break;
    case "lobby":
      if (!Array.isArray(message.players) || !message.players.every(player => record(player) && typeof player.id === "string" && typeof player.name === "string")) throw new Error("Invalid lobby response."); break;
    case "state":
      if (!record(message.match) || typeof message.match.id !== "string" || !record(message.match.own) || !record(message.match.other) || !Number.isSafeInteger(message.match.round) || !["player", "opponent"].includes(String(message.match.ownSide))) throw new Error("Invalid match response."); break;
    case "frame":
      if (!record(message.frame) || typeof message.frame.matchId !== "string" || !Array.isArray(message.frame.units) || !Array.isArray(message.frame.events) || !Number.isSafeInteger(message.frame.sequence)) throw new Error("Invalid battle response."); break;
    case "chat": if (typeof message.text !== "string" || typeof message.name !== "string" || typeof message.matchId !== "string" || typeof message.id !== "string") throw new Error("Invalid chat response."); break;
    case "error": case "left": if (typeof message.message !== "string") throw new Error("Invalid multiplayer error."); break;
    case "pong": break;
    default: throw new Error(`Unhandled multiplayer response: ${String(message.type)}`);
  }
  return message as ServerMessage;
}
